import React, { useState, useEffect, useRef } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import ApiServices from "../Services/ApiServices";
import { motion } from "framer-motion";
import Select from "react-select";
import debounce from "lodash/debounce";
import extractErrorMessage from "../../Helpers/APIHelper";
import { toastHelper } from "../../Helpers/toastHelper";
import FileUploader from "../Shared/FileUploader";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  faUser,
  faSpinner,
  faUserPlus,
  faGraduationCap,
  faEnvelope,
  faPhone,
  faVenusMars,
  faChalkboardTeacher,
  faBookOpen,
} from "@fortawesome/free-solid-svg-icons";

const validationSchema = Yup.object().shape({
  fullName: Yup.string()
    .required("الاسم مطلوب")
    .min(3, "الاسم يجب أن يكون 3 أحرف على الأقل")
    .max(50, "الاسم يجب أن لا يتجاوز 50 حرف"),
  email: Yup.string()
    .required("البريد الإلكتروني ولي الأمر مطلوب")
    .email("البريد الإلكتروني ولي الأمر غير صالح"),
  phone: Yup.string()
    .required("رقم الهاتف ولي الأمر مطلوب")
    .matches(/^[0-9]{11}$/, "رقم الهاتف ولي الأمر يجب أن يكون 11 أرقام"),
  gender: Yup.string().required("الجنس مطلوب"),
  user_class: Yup.string().required("الصف مطلوب"),
  teacher_id: Yup.string().required("المعلم مطلوب"),
  material_id: Yup.string().required("المادة مطلوبة"),
});

const fadeUp = {
  hidden: { opacity: 0, y: 32 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.65, delay: i * 0.12, ease: [0.22, 1, 0.36, 1] },
  }),
};

const normalizeTeacherId = (value) => {
  if (value === undefined || value === null) return null;
  const s = String(value).trim();
  if (!s) return null;
  return s;
};

const mergeUniqueTeacherOptions = (options, optionToAdd) => {
  if (!optionToAdd) return options;
  const addId = String(optionToAdd.value);
  const filtered = (options || []).filter((o) => String(o.value) !== addId);
  return [optionToAdd, ...filtered];
};

const RegisterNow = ({ teacherId = null }) => {
  const [userClasses, setUserClasses] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [loading, setLoading] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);
  const [teacherSearchValue, setTeacherSearchValue] = useState("");
  const [isLoadingTeachers, setIsLoadingTeachers] = useState(false);

  const [lockedTeacherOption, setLockedTeacherOption] = useState(null);
  const [isTeacherLocked, setIsTeacherLocked] = useState(false);
  const navigate = useNavigate();
  const formikRef = useRef(null);
  const [searchParams] = useSearchParams();

  const teacherIdFromProp = normalizeTeacherId(teacherId);
  const teacherIdFromUrl = normalizeTeacherId(searchParams.get("teacherId"));
  const teacherIdCandidate = teacherIdFromProp ?? teacherIdFromUrl;

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [classesResponse, teachersResponse, materialsResponse] =
          await Promise.all([
            ApiServices.get("lookup/userClasses"),
            ApiServices.get("lookup/teachers"),
            ApiServices.get("lookup/materials"),
          ]);
        setUserClasses(classesResponse || []);
        const allTeachers = teachersResponse || [];
        let limitedTeachers = allTeachers.slice(0, 4).map((teacher) => ({
          value: String(teacher.id),
          label: teacher.name ?? teacher.full_name,
        }));

        if (teacherIdCandidate) {
          const matchedTeacher = allTeachers.find(
            (t) => String(t.id) === String(teacherIdCandidate)
          );
          if (matchedTeacher) {
            const option = {
              value: String(matchedTeacher.id),
              label: matchedTeacher.name ?? matchedTeacher.full_name,
            };
            setLockedTeacherOption(option);
            limitedTeachers = mergeUniqueTeacherOptions(limitedTeachers, option);
            const shouldLock = Boolean(teacherIdFromProp);
            setIsTeacherLocked(shouldLock);
            formikRef.current?.setFieldValue(
              "teacher_id",
              String(matchedTeacher.id),
              false
            );
          } else {
            setLockedTeacherOption(null);
            setIsTeacherLocked(false);
            formikRef.current?.setFieldValue("teacher_id", "", false);
          }
        } else {
          setLockedTeacherOption(null);
          setIsTeacherLocked(false);
        }

        setTeachers(limitedTeachers);
        setMaterials(materialsResponse || []);
      } catch (error) {
        const title = error?.title || error?.message || "";
        if (title.toLowerCase().includes("validation errors")) {
          const errMessage = extractErrorMessage(error);
          toastHelper.error(errMessage);
        } else {
          toastHelper.error(error);
        }
      }
    };

    fetchData();
  }, [teacherIdCandidate, teacherIdFromProp]);

  const searchTeachers = debounce(async (searchTerm) => {
    if (isTeacherLocked) return;

    if (!searchTerm || searchTerm.trim() === "") {
      try {
        const response = await ApiServices.get("lookup/teachers");
        const allTeachers = response || [];
        let limitedTeachers = allTeachers.slice(0, 4).map((teacher) => ({
          value: String(teacher.id),
          label: teacher.name ?? teacher.full_name,
        }));
        limitedTeachers = mergeUniqueTeacherOptions(
          limitedTeachers,
          lockedTeacherOption
        );
        setTeachers(limitedTeachers);
      } catch (error) {

      }
      return;
    }

    try {
      setIsLoadingTeachers(true);
      const response = await ApiServices.get(
        `Lookup/Teachers/filter?Name=${encodeURIComponent(searchTerm.trim())}`
      );
      if (response && response.check && response.data) {
        const formattedTeachers = response.data.map((teacher) => ({
          value: String(teacher.id),
          label: teacher.name ?? teacher.full_name,
        }));
        setTeachers(mergeUniqueTeacherOptions(formattedTeachers, lockedTeacherOption));
      } else if (response && Array.isArray(response)) {
        const formattedTeachers = response.map((teacher) => ({
          value: String(teacher.id),
          label: teacher.name ?? teacher.full_name,
        }));
        setTeachers(mergeUniqueTeacherOptions(formattedTeachers, lockedTeacherOption));
      } else {
        setTeachers([]);
      }
    } catch (error) {

      setTeachers([]);
    } finally {
      setIsLoadingTeachers(false);
    }
  }, 500);

  const handleTeacherSearch = (inputValue) => {
    if (isTeacherLocked) return;
    setTeacherSearchValue(inputValue);
    searchTeachers(inputValue);
  };

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    setLoading(true);
    const formData = new FormData();
    formData.append("fullName", values.fullName);
    formData.append("email", values.email);
    formData.append("phone", values.phone);
    formData.append("gender", values.gender);
    formData.append("user_class", values.user_class);
    formData.append("material_id", values.material_id);
    formData.append("teacher_id", values.teacher_id);
    if (values.profile_picture) {
      formData.append("profile_picture", values.profile_picture);
    }
    try {
      await ApiServices.post("User/Add", formData, true);
      toastHelper.success("تم التسجيل بنجاح برجاء مراجعة حسابك مع المعلم");
      navigate("/");
    } catch (error) {
      toastHelper.error(error);
    } finally {
      setLoading(false);
      setSubmitting(false);
    }
  };

  const handleImageChange = (event, setFieldValue) => {
    const file = event.currentTarget.files[0];
    if (file) {
      setFieldValue("profile_picture", file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewImage(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.currentTarget.classList.add("dragging");
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.currentTarget.classList.remove("dragging");
  };

  const handleDrop = (e, setFieldValue) => {
    e.preventDefault();
    e.currentTarget.classList.remove("dragging");
    const file = e.dataTransfer.files[0];
    if (file && file.type.startsWith("image/")) {
      setFieldValue("profile_picture", file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewImage(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const selectStyles = {
    control: (base, state) => ({
      ...base,
      backgroundColor: "#f8fafc",
      border: state.isFocused ? "1.5px solid #6366f1" : "1.5px solid rgba(15,23,42,0.1)",
      boxShadow: state.isFocused ? "0 0 0 4px rgba(232,146,10,0.08)" : "none",
      borderRadius: "14px",
      minHeight: "52px",
      transition: "all 0.25s",
      "&:hover": {
        borderColor: state.isFocused ? "#6366f1" : "rgba(15,23,42,0.2)",
      },
    }),
    placeholder: (base) => ({
      ...base,
      color: "#94a3b8",
      fontSize: "0.95rem",
      fontWeight: "500",
    }),
    singleValue: (base) => ({
      ...base,
      color: "#0f172a",
      fontSize: "0.98rem",
      fontWeight: "700",
    }),
    menu: (base) => ({
      ...base,
      backgroundColor: "#fff",
      borderRadius: "16px",
      boxShadow: "0 12px 32px rgba(15,23,42,0.12)",
      border: "1px solid rgba(15,23,42,0.08)",
      overflow: "hidden",
      zIndex: 10,
    }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isSelected ? "#6366f1" : state.isFocused ? "#f8fafc" : "transparent",
      color: state.isSelected ? "#fff" : "#0f172a",
      fontWeight: "700",
      padding: "12px 18px",
      cursor: "pointer",
      "&:hover": {
        backgroundColor: state.isSelected ? "#6366f1" : "#f8fafc",
      },
    }),
  };

  return (
    <section className="rn-root" id="register">
      <div className="rn-container">

        {/* ── header ── */}
        <motion.div
          className="rn-header"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
        >
          <motion.div className="rn-eyebrow" variants={fadeUp} custom={0}>
            <span className="rn-eyebrow-dot" />
            انضم إلينا
          </motion.div>
          <motion.h2 className="rn-title" variants={fadeUp} custom={1}>
            سجل مع <em>معلمك الآن</em>
          </motion.h2>
          <motion.p className="rn-subtitle" variants={fadeUp} custom={2}>
            انضم إلى منصتنا التعليمية وابدأ رحلتك التعليمية مع أفضل المعلمين
          </motion.p>
        </motion.div>

        {/* ── form ── */}
        <motion.div
          className="rn-form-wrap"
          initial={{ opacity: 0, y: 36 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.1 }}
          transition={{ duration: 0.65, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
        >
          <Formik
            innerRef={formikRef}
            initialValues={{
              fullName: "",
              email: "",
              phone: "",
              gender: "",
              user_class: "",
              teacher_id: teacherIdCandidate ? String(teacherIdCandidate) : "",
              material_id: "",
              profile_picture: null,
            }}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
          >
            {({ isSubmitting, errors, touched, setFieldValue, values }) => (
              <Form className="rn-form" autoComplete="new-password">

                <div className="rn-grid">
                  {/* Personal Info */}
                  <div className="rn-section">
                    <div className="rn-section-header">
                      <FontAwesomeIcon icon={faUser} className="rn-section-icon" />
                      <h3>المعلومات الشخصية</h3>
                    </div>

                    <div className="rn-fields-stack">
                      <div className="rn-field">
                        <label>الاسم الكامل</label>
                        <div className="rn-input-box">
                          <Field
                            name="fullName"
                            type="text"
                            placeholder="مثال: أحمد محمد"
                            autoComplete="new-password"
                            className={`rn-input ${errors.fullName && touched.fullName ? "rn-input-err" : ""}`}
                          />
                          <FontAwesomeIcon icon={faUser} className="rn-field-icon" />
                        </div>
                        <ErrorMessage name="fullName" component="div" className="rn-err" />
                      </div>

                      <div className="rn-field">
                        <label>البريد الإلكتروني ولي الأمر</label>
                        <div className="rn-input-box">
                          <Field
                            name="email"
                            type="email"
                            placeholder="example@email.com"
                            autoComplete="new-password"
                            className={`rn-input ${errors.email && touched.email ? "rn-input-err" : ""}`}
                          />
                          <FontAwesomeIcon icon={faEnvelope} className="rn-field-icon" />
                        </div>
                        <ErrorMessage name="email" component="div" className="rn-err" />
                      </div>

                      <div className="rn-field">
                        <label>رقم الهاتف ولي الأمر</label>
                        <div className="rn-input-box">
                          <Field
                            name="phone"
                            type="tel"
                            maxLength="11"
                            placeholder="01xxxxxxxxx"
                            autoComplete="new-password"
                            className={`rn-input ${errors.phone && touched.phone ? "rn-input-err" : ""}`}
                          />
                          <FontAwesomeIcon icon={faPhone} className="rn-field-icon" />
                        </div>
                        <ErrorMessage name="phone" component="div" className="rn-err" />
                      </div>

                      <div className="rn-field">
                        <label>الجنس</label>
                        <div className="rn-input-box">
                          <Select
                            options={[
                              { value: "1", label: "ذكر" },
                              { value: "2", label: "أنثى" },
                            ]}
                            placeholder="اختر الجنس"
                            styles={selectStyles}
                            onChange={(opt) => setFieldValue("gender", opt ? opt.value : "")}
                            value={[
                              { value: "1", label: "ذكر" },
                              { value: "2", label: "أنثى" },
                            ].find(o => o.value === values.gender)}
                          />
                          <FontAwesomeIcon icon={faVenusMars} className="rn-field-icon" />
                        </div>
                        <ErrorMessage name="gender" component="div" className="rn-err" />
                      </div>
                    </div>
                  </div>

                  {/* Academic Info */}
                  <div className="rn-section">
                    <div className="rn-section-header">
                      <FontAwesomeIcon icon={faGraduationCap} className="rn-section-icon" />
                      <h3>المعلومات الأكاديمية</h3>
                    </div>

                    <div className="rn-fields-stack">
                      <div className="rn-field">
                        <label>الصف الدراسي</label>
                        <div className="rn-input-box">
                          <Select
                            options={userClasses.map(c => ({ value: c.id, label: c.name }))}
                            placeholder="اختر الصف"
                            styles={selectStyles}
                            onChange={(opt) => setFieldValue("user_class", opt ? opt.value : "")}
                            value={userClasses.map(c => ({ value: c.id, label: c.name })).find(o => o.value === values.user_class)}
                          />
                          <FontAwesomeIcon icon={faGraduationCap} className="rn-field-icon" />
                        </div>
                        <ErrorMessage name="user_class" component="div" className="rn-err" />
                      </div>

                      <div className="rn-field">
                        <label>المادة العلمية</label>
                        <div className="rn-input-box">
                          <Select
                            options={materials.map(m => ({ value: m.id, label: m.name }))}
                            placeholder="اختر المادة"
                            styles={selectStyles}
                            onChange={(opt) => setFieldValue("material_id", opt ? opt.value : "")}
                            value={materials.map(m => ({ value: m.id, label: m.name })).find(o => o.value === values.material_id)}
                          />
                          <FontAwesomeIcon icon={faBookOpen} className="rn-field-icon" />
                        </div>
                        <ErrorMessage name="material_id" component="div" className="rn-err" />
                      </div>

                      <div className="rn-field">
                        <label>المعلم</label>
                        <div className="rn-input-box">
                          <Select
                            options={teachers}
                            isLoading={isLoadingTeachers}
                            isDisabled={isTeacherLocked}
                            onInputChange={handleTeacherSearch}
                            inputValue={isTeacherLocked ? "" : teacherSearchValue}
                            placeholder="ابحث عن معلم..."
                            styles={selectStyles}
                            onChange={(opt) => setFieldValue("teacher_id", opt ? String(opt.value) : "")}
                            value={teachers.find(o => o.value === values.teacher_id)}
                          />
                          <FontAwesomeIcon icon={faChalkboardTeacher} className="rn-field-icon" />
                        </div>
                        <ErrorMessage name="teacher_id" component="div" className="rn-err" />
                      </div>

                      {/* Photo */}
                      <div className="rn-field rn-photo-field">
                        <label>(اختياري) الصورة الشخصية</label>
                        <FileUploader
                          previewUrl={previewImage}
                          onFileChange={(e) => handleImageChange(e, setFieldValue)}
                          onDragOver={handleDragOver}
                          onDragLeave={handleDragLeave}
                          onDrop={(e) => handleDrop(e, setFieldValue)}
                          imageDragOver={false}
                          accept="image/*"
                          placeholder="اختر صورة شخصية"
                          subText="اسحب وأفلت الصورة هنا أو انقر للاختيار"
                          buttonText="اختيار صورة"
                          changeButtonText="تغيير الصورة"
                          id="profile_picture"
                        />
                        <ErrorMessage name="profile_picture" component="div" className="rn-err" />
                      </div>
                    </div>
                  </div>
                </div>

                <div className="rn-submit-wrap">
                  <motion.button
                    type="submit"
                    className="rn-submit"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    disabled={isSubmitting || loading}
                  >
                    {isSubmitting || loading ? (
                      <>
                        <FontAwesomeIcon icon={faSpinner} spin />
                        <span>جاري التسجيل...</span>
                      </>
                    ) : (
                      <>
                        <FontAwesomeIcon icon={faUserPlus} />
                        <span>سجل الآن</span>
                      </>
                    )}
                  </motion.button>
                </div>

              </Form>
            )}
          </Formik>
        </motion.div>
      </div>

      <style>{`
        .rn-root {
            font-family: "Zain", sans-serif;
          direction: rtl;
          padding: 100px 0 120px;
          background: #fff;
          overflow: hidden;
        }

        .rn-container {
          max-width: 1260px; margin: 0 auto; padding: 0 40px;
        }
        @media(max-width:768px){ .rn-container { padding: 0 20px; } }

        /* header */
        .rn-header { text-align: center; margin-bottom: 64px; }
        .rn-eyebrow {
          display: inline-flex; align-items: center; gap: 10px;
          color: #6366f1; font-weight: 800; font-size: 0.82rem;
          letter-spacing: 0.15em; text-transform: uppercase; margin-bottom: 22px;
        }
        .rn-eyebrow-dot {
          width: 8px; height: 8px; border-radius: 50%; background: #6366f1;
          animation: rn-pulse 2.2s ease-in-out infinite;
        }
        @keyframes rn-pulse {
          0%,100%{ box-shadow: 0 0 0 0 rgba(232,146,10,0.45); }
          50%{ box-shadow: 0 0 0 9px rgba(232,146,10,0); }
        }
        .rn-title {
          font-size: clamp(2.2rem, 4vw, 3.4rem);
          font-weight: 900; color: #0f172a;
          margin: 0 0 18px; line-height: 1.2;
        }
        .rn-title em {
          font-style: normal;
          background: linear-gradient(90deg, #6366f1 0%, #6366f1 55%, #6366f1 100%);
          -webkit-background-clip: text; -webkit-text-fill-color: transparent;
          background-clip: text;
        }
        .rn-subtitle {
          font-size: 1.08rem; color: #64748b; line-height: 1.85; margin: 0;
        }

        /* form wrap */
        .rn-form-wrap {
          background: #fff;
          border-radius: 32px;
          border: 1px solid rgba(15,23,42,0.08);
          padding: 48px;
          box-shadow: 0 12px 48px rgba(15,23,42,0.06);
        }
        @media(max-width:768px){ .rn-form-wrap { padding: 28px 20px; } }

        .rn-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 40px;
        }
        @media(max-width:992px){ .rn-grid { grid-template-columns: 1fr; gap: 48px; } }

        .rn-section-header {
          display: flex; align-items: center; gap: 14px;
          margin-bottom: 28px; padding-bottom: 16px;
          border-bottom: 1.5px solid rgba(15,23,42,0.05);
        }
        .rn-section-icon {
          width: 44px; height: 44px; border-radius: 12px;
          background: rgba(232,146,10,0.1); color: #6366f1;
          display: flex; align-items: center; justify-content: center;
          padding: 10px;
        }
        .rn-section-header h3 {
          font-size: 1.3rem; font-weight: 800; color: #0f172a; margin: 0;
        }

        .rn-fields-stack {
          display: flex; flex-direction: column; gap: 24px;
        }

        .rn-field label {
          display: block; margin-bottom: 8px;
          color: #334155; font-weight: 700; font-size: 0.92rem;
        }
        .rn-input-box { position: relative; }
        
        .rn-input {
          width: 100%;
          padding: 13px 18px 13px 46px;
          border-radius: 14px;
          border: 1.5px solid rgba(15,23,42,0.1);
          background: #f8fafc;
          font-size: 0.98rem; color: #0f172a;
          font-family: inherit; font-weight: 700;
          transition: all 0.25s;
        }
        .rn-input::placeholder { color: #94a3b8; font-weight: 500; }
        .rn-input:focus {
          outline: none; border-color: #6366f1;
          background: #fff; box-shadow: 0 0 0 4px rgba(232,146,10,0.08);
        }
        .rn-input-err { border-color: #ef4444 !important; background: #fef2f2; }

        .rn-field-icon {
          position: absolute; left: 16px; top: 50%;
          transform: translateY(-50%);
          color: #94a3b8; font-size: 1.05rem;
          pointer-events: none; transition: color 0.25s;
        }
        .rn-input:focus ~ .rn-field-icon { color: #6366f1; }

        .rn-err {
          color: #ef4444; font-size: 0.82rem; font-weight: 600; margin-top: 6px;
        }

        .rn-photo-field { margin-top: 8px; }

        /* submit */
        .rn-submit-wrap {
          margin-top: 48px; display: flex; justify-content: center;
        }
        .rn-submit {
          min-width: 240px; padding: 16px 48px;
          background: #6366f1;
          color: #fff; border: none; border-radius: 16px;
          font-family: inherit; font-size: 1.15rem; font-weight: 900;
          cursor: pointer;
          display: flex; align-items: center; justify-content: center; gap: 12px;
          box-shadow: 0 12px 32px -8px rgba(99,102,241,0.45);
          transition: box-shadow 0.3s;
        }
        .rn-submit:hover { box-shadow: 0 20px 40px -8px rgba(99,102,241,0.55); }
        .rn-submit:disabled { opacity: 0.7; cursor: not-allowed; }

        /* FileUploader overrides for rn namespace */
        .rn-photo-field :global(.file-upload-container) {
          border-radius: 18px !important;
          border: 2px dashed rgba(15,23,42,0.1) !important;
          background: #f8fafc !important;
        }
        .rn-photo-field :global(.upload-button) {
          background: #0f172a !important;
          border-radius: 12px !important;
        }
      `}</style>
    </section>
  );
};

export default RegisterNow;
